import { useEffect, useRef, useState } from 'react';
import { ScrollView, StyleSheet, Platform } from 'react-native';
import { Box, Typography, Button, useTheme, useMediaQuery, Fab } from '@mui/material';
import { useIsFocused } from '@react-navigation/native';
import { navigationStore, ScreenHeroConfig } from '../../../../app/stores/Navigation';
import { useStoreConfig } from '../../../../contexts/StoreConfigContext';
import { useScreenComponent } from '../../../../app/hooks/custom/useScreenComponent';
import { ScreenComponent } from '../../../../models/enums/ScreenComponents';
import { AppLogo } from '../../../../components/AppLogo';
import { VideoBackground } from '../../../../components/VideoBackground';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import VerifiedIcon from '@mui/icons-material/Verified';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import StorefrontIcon from '@mui/icons-material/Storefront';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import InventoryIcon from '@mui/icons-material/Inventory';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

// CSS Keyframes for animations
const keyframes = `
    @keyframes fadeInUp {
        from {
            opacity: 0;
            transform: translateY(40px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }
    
    @keyframes fadeInLeft {
        from {
            opacity: 0;
            transform: translateX(-40px);
        }
        to {
            opacity: 1;
            transform: translateX(0);
        }
    }
    
    @keyframes fadeInRight {
        from {
            opacity: 0;
            transform: translateX(40px);
        }
        to {
            opacity: 1;
            transform: translateX(0);
        }
    }
    
    @keyframes scaleIn {
        from {
            opacity: 0;
            transform: scale(0.8);
        }
        to {
            opacity: 1;
            transform: scale(1);
        }
    }
    
    @keyframes float {
        0%, 100% {
            transform: translateY(0px);
        }
        50% {
            transform: translateY(-10px);
        }
    }
    
    @keyframes pulse {
        0%, 100% {
            opacity: 1;
        }
        50% {
            opacity: 0.7;
        }
    }
`;

// Hook to detect when element is in viewport
const useInView = (threshold = 0.1) => {
    const ref = useRef<HTMLDivElement>(null);
    const [isInView, setIsInView] = useState(false);

    useEffect(() => {
        if (Platform.OS !== 'web') {
            setIsInView(true);
            return;
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsInView(true);
                }
            },
            { threshold }
        );

        if (ref.current) {
            observer.observe(ref.current);
        }

        return () => observer.disconnect();
    }, [threshold]);

    return { ref, isInView };
};

/**
 * Home Screen - Cape Roots Landing Page
 * A modern, animated landing page for Cape Roots fruit & vegetable wholesaler
 */
export const CapeRootsHome = () => {
    const theme = useTheme();
    const isFocused = useIsFocused();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const { updateSplashLoaded, updateShowNavigationBar, screenHeroConfigs } = navigationStore((state) => state);
    const { config } = useStoreConfig();
    const { navigateToScreen } = useScreenComponent();

    // Get hero config for this screen (video URL comes from database via API)
    const heroConfig: ScreenHeroConfig = screenHeroConfigs['CapeRootsHome'] || {};

    useEffect(() => {
        updateSplashLoaded(true);
    }, []);

    useEffect(() => {
        if (isFocused) {
            updateShowNavigationBar(true);
        }
    }, [isFocused]);

    // Inject keyframes into document
    useEffect(() => {
        if (Platform.OS === 'web') {
            const styleSheet = document.createElement('style');
            styleSheet.innerText = keyframes;
            document.head.appendChild(styleSheet);
            return () => {
                document.head.removeChild(styleSheet);
            };
        }
    }, []);

    return (
        <ScrollView
            style={{
                flex: 1,
                backgroundColor: theme.palette.background.default,
            }}
            contentContainerStyle={styles.scrollContent}
        >
            {/* Hero Section */}
            <HeroSection 
                config={config} 
                theme={theme} 
                isMobile={isMobile}
                isTablet={isTablet}
                heroConfig={heroConfig}
                onShopNow={() => navigateToScreen(ScreenComponent.Shop)}
            />

            {/* Value Proposition Section */}
            <ValuePropositionSection theme={theme} config={config} isMobile={isMobile} />

            {/* Services Section */}
            <ServicesSection 
                theme={theme} 
                config={config} 
                isMobile={isMobile}
                isTablet={isTablet}
                onNavigate={navigateToScreen}
            />

            {/* About Section */}
            <AboutSection theme={theme} config={config} isMobile={isMobile} isTablet={isTablet} />

            {/* Why Choose Us Section */}
            <WhyChooseUsSection theme={theme} config={config} isMobile={isMobile} isTablet={isTablet} />

            {/* Contact Section */}
            <ContactSection theme={theme} config={config} isMobile={isMobile} isTablet={isTablet} />

            {/* CTA Section */}
            <CTASection 
                theme={theme} 
                config={config} 
                isMobile={isMobile}
                onShopNow={() => navigateToScreen(ScreenComponent.Shop)}
            />

            {/* Scroll FAB */}
            <Fab
                size={isMobile ? 'medium' : 'large'}
                onClick={() => {
                    const aboutSection = document.getElementById('about-section');
                    aboutSection?.scrollIntoView({ behavior: 'smooth' });
                }}
                sx={{
                    position: 'fixed',
                    bottom: isMobile ? 80 : 70,
                    right: isMobile ? 16 : 70,
                    backgroundColor: config.primaryColor,
                    color: 'white',
                    boxShadow: `0 8px 32px ${config.primaryColor}50, 0 4px 12px rgba(0,0,0,0.15)`,
                    animation: 'float 2s ease-in-out infinite',
                    '&:hover': {
                        backgroundColor: config.primaryColor,
                        transform: 'scale(1.1)',
                        boxShadow: `0 12px 40px ${config.primaryColor}70, 0 6px 16px rgba(0,0,0,0.2)`,
                    },
                    transition: 'all 0.3s ease',
                    zIndex: 1000,
                }}
            >
                <KeyboardArrowDownIcon sx={{ fontSize: isMobile ? 24 : 28 }} />
            </Fab>
        </ScrollView>
    );
};

// Hero Section Component
interface HeroSectionProps {
    config: any;
    theme: any;
    isMobile: boolean;
    isTablet: boolean;
    heroConfig?: ScreenHeroConfig;
    onShopNow: () => void;
}

const HeroSection = ({ config, theme, isMobile, isTablet, heroConfig, onShopNow }: HeroSectionProps) => {
    const { ref, isInView } = useInView(0.1);
    const hasVideoBackground = !!heroConfig?.heroVideoUrl || !!heroConfig?.heroImageUrl;

    // Content for the hero section
    const heroContent = (
        <>
            <Box
                sx={{
                    opacity: isInView ? 1 : 0,
                    animation: isInView ? 'scaleIn 0.8s ease-out forwards' : 'none',
                    marginBottom: 3,
                }}
            >
                <AppLogo 
                    size={isMobile ? 100 : 140} 
                    showText={false}
                    sx={{ 
                        animation: 'float 3s ease-in-out infinite',
                    }}
                />
            </Box>

            <Typography 
                variant={isMobile ? 'h5' : 'h4'}
                sx={{ 
                    fontWeight: 300,
                    color: hasVideoBackground ? '#FFFFFF' : config.primaryColor,
                    marginBottom: 2,
                    letterSpacing: isMobile ? 2 : 4,
                    textTransform: 'uppercase',
                    opacity: isInView ? 1 : 0,
                    animation: isInView ? 'fadeInUp 0.8s ease-out 0.2s forwards' : 'none',
                    animationFillMode: 'backwards',
                    textShadow: hasVideoBackground ? '0 2px 10px rgba(0,0,0,0.5)' : 'none',
                }}
            >
                Rooted in Quality
            </Typography>
            
            <Typography 
                variant={isMobile ? 'h3' : 'h2'}
                sx={{ 
                    fontWeight: 700,
                    marginBottom: 2,
                    color: hasVideoBackground ? '#FFFFFF' : theme.palette.text.primary,
                    opacity: isInView ? 1 : 0,
                    animation: isInView ? 'fadeInUp 0.8s ease-out 0.4s forwards' : 'none',
                    animationFillMode: 'backwards',
                    maxWidth: 800,
                    lineHeight: 1.2,
                    textShadow: hasVideoBackground ? '0 2px 20px rgba(0,0,0,0.6)' : 'none',
                }}
            >
                Delivered with Excellence
            </Typography>
            
            <Typography 
                variant={isMobile ? 'body1' : 'h6'}
                sx={{ 
                    color: hasVideoBackground ? 'rgba(255,255,255,0.9)' : theme.palette.text.secondary,
                    marginBottom: 5,
                    maxWidth: 600,
                    fontWeight: 400,
                    lineHeight: 1.8,
                    opacity: isInView ? 1 : 0,
                    animation: isInView ? 'fadeInUp 0.8s ease-out 0.6s forwards' : 'none',
                    animationFillMode: 'backwards',
                    px: 2,
                    textShadow: hasVideoBackground ? '0 1px 8px rgba(0,0,0,0.5)' : 'none',
                }}
            >
                Nourishing the hospitality industry one harvest at a time. 
                Fresh, locally sourced produce for businesses across Cape Town.
            </Typography>

            <Box 
                sx={{ 
                    display: 'flex', 
                    gap: 2, 
                    flexWrap: 'wrap', 
                    justifyContent: 'center',
                    opacity: isInView ? 1 : 0,
                    animation: isInView ? 'fadeInUp 0.8s ease-out 0.8s forwards' : 'none',
                    animationFillMode: 'backwards',
                }}
            >
                <Button
                    variant='contained'
                    size='large'
                    onClick={onShopNow}
                    sx={{
                        backgroundColor: hasVideoBackground ? '#FFFFFF' : config.primaryColor,
                        color: hasVideoBackground ? config.primaryColor : '#FFFFFF',
                        '&:hover': {
                            backgroundColor: hasVideoBackground ? 'rgba(255,255,255,0.9)' : config.primaryColor,
                            transform: 'translateY(-2px)',
                            boxShadow: hasVideoBackground
                                ? '0 8px 25px rgba(255,255,255,0.4)'
                                : `0 8px 25px ${config.primaryColor}40`,
                        },
                        paddingX: isMobile ? 4 : 6,
                        paddingY: 1.5,
                        fontSize: isMobile ? '1rem' : '1.1rem',
                        fontWeight: 600,
                        borderRadius: 3,
                        transition: 'all 0.3s ease',
                    }}
                >
                    Browse Products
                </Button>
                
                <Button
                    variant='outlined'
                    size='large'
                    onClick={() => {
                        const aboutSection = document.getElementById('about-section');
                        aboutSection?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    sx={{
                        borderColor: hasVideoBackground ? '#FFFFFF' : config.primaryColor,
                        borderWidth: 2,
                        color: hasVideoBackground ? '#FFFFFF' : config.primaryColor,
                        '&:hover': {
                            borderColor: hasVideoBackground ? '#FFFFFF' : config.primaryColor,
                            borderWidth: 2,
                            backgroundColor: hasVideoBackground
                                ? 'rgba(255,255,255,0.15)'
                                : `${config.primaryColor}10`,
                            transform: 'translateY(-2px)',
                        },
                        paddingX: isMobile ? 4 : 6,
                        paddingY: 1.5,
                        fontSize: isMobile ? '1rem' : '1.1rem',
                        fontWeight: 600,
                        borderRadius: 3,
                        transition: 'all 0.3s ease',
                    }}
                >
                    Learn More
                </Button>
            </Box>
        </>
    );

    // Render with video/image background if configured
    if (hasVideoBackground) {
        return (
            <Box ref={ref}>
                <VideoBackground
                    videoUrl={heroConfig?.heroVideoUrl}
                    imageUrl={heroConfig?.heroImageUrl}
                    overlayOpacity={heroConfig?.heroOverlayOpacity ?? 0.4}
                    minHeight={isMobile ? '90vh' : '85vh'}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: isMobile ? 3 : 6,
                        textAlign: 'center',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flex: 1,
                        }}
                    >
                        {heroContent}
                    </Box>
                </VideoBackground>
            </Box>
        );
    }

    // Render default gradient background
    return (
        <Box
            ref={ref}
            sx={{
                minHeight: isMobile ? '90vh' : '85vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: isMobile ? 3 : 6,
                textAlign: 'center',
                position: 'relative',
                overflow: 'hidden',
                background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${config.primaryColor}08 50%, ${theme.palette.background.default} 100%)`,
            }}
        >
            {/* Decorative background elements */}
            <Box
                sx={{
                    position: 'absolute',
                    top: '10%',
                    left: '5%',
                    width: isMobile ? 100 : 200,
                    height: isMobile ? 100 : 200,
                    borderRadius: '50%',
                    background: `${config.primaryColor}10`,
                    filter: 'blur(60px)',
                    animation: 'pulse 4s ease-in-out infinite',
                }}
            />
            <Box
                sx={{
                    position: 'absolute',
                    bottom: '15%',
                    right: '10%',
                    width: isMobile ? 150 : 300,
                    height: isMobile ? 150 : 300,
                    borderRadius: '50%',
                    background: `${config.primaryColor}08`,
                    filter: 'blur(80px)',
                    animation: 'pulse 5s ease-in-out infinite',
                }}
            />

            {heroContent}
        </Box>
    );
};

// Value Proposition Section
const ValuePropositionSection = ({ theme, config, isMobile }: any) => {
    const { ref, isInView } = useInView(0.2);
    
    const stats = [
        { value: 'Fresh', label: 'Daily Deliveries' },
        { value: 'Local', label: 'SA Sourced' },
        { value: '4AM', label: 'Early Start' },
        { value: 'CBD', label: 'Cape Town Coverage' },
    ];

    return (
        <Box
            ref={ref}
            sx={{
                py: isMobile ? 6 : 8,
                px: isMobile ? 2 : 4,
                backgroundColor: config.primaryColor,
            }}
        >
            <Box sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'center',
                alignItems: 'center',
                gap: isMobile ? 4 : 8,
                maxWidth: 1200,
                margin: '0 auto',
            }}>
                {stats.map((stat, index) => (
                    <Box
                        key={stat.label}
                        sx={{
                            textAlign: 'center',
                            opacity: isInView ? 1 : 0,
                            animation: isInView ? `fadeInUp 0.6s ease-out ${index * 0.1}s forwards` : 'none',
                            animationFillMode: 'backwards',
                        }}
                    >
                        <Typography 
                            variant={isMobile ? 'h4' : 'h3'}
                            sx={{ 
                                fontWeight: 700, 
                                color: 'white',
                                marginBottom: 0.5,
                            }}
                        >
                            {stat.value}
                        </Typography>
                        <Typography 
                            variant='body2'
                            sx={{ 
                                color: 'rgba(255,255,255,0.85)',
                                fontWeight: 500,
                                textTransform: 'uppercase',
                                letterSpacing: 1,
                            }}
                        >
                            {stat.label}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

// Services Section
const ServicesSection = ({ theme, config, isMobile, isTablet, onNavigate }: any) => {
    const { ref, isInView } = useInView(0.1);

    const services = [
        {
            icon: <InventoryIcon sx={{ fontSize: isMobile ? 40 : 50 }} />,
            title: 'Groceries',
            description: 'Premium selection of fresh produce, pantry essentials, and specialty items for your business needs.',
            action: () => onNavigate(ScreenComponent.Shop),
        },
        {
            icon: <StorefrontIcon sx={{ fontSize: isMobile ? 40 : 50 }} />,
            title: 'Products',
            description: 'Vibrant tomatoes, crisp cucumbers, succulent berries, and seasonal delights sourced locally.',
            action: () => onNavigate(ScreenComponent.Shop),
        },
        {
            icon: <RestaurantIcon sx={{ fontSize: isMobile ? 40 : 50 }} />,
            title: 'Restaurants',
            description: 'Tailored wholesale solutions for restaurants, cafes, and hospitality businesses across Cape Town.',
            action: () => onNavigate(ScreenComponent.Shop),
        },
    ];

    return (
        <Box
            ref={ref}
            sx={{
                py: isMobile ? 8 : 12,
                px: isMobile ? 2 : 4,
                backgroundColor: theme.palette.background.paper,
            }}
        >
            <Box sx={{ maxWidth: 1200, margin: '0 auto' }}>
                <Box
                    sx={{
                        textAlign: 'center',
                        marginBottom: isMobile ? 5 : 8,
                        opacity: isInView ? 1 : 0,
                        animation: isInView ? 'fadeInUp 0.8s ease-out forwards' : 'none',
                    }}
                >
                    <Typography 
                        variant='overline'
                        sx={{ 
                            color: config.primaryColor,
                            fontWeight: 600,
                            letterSpacing: 3,
                            marginBottom: 1,
                            display: 'block',
                        }}
                    >
                        Our Services
                    </Typography>
                    <Typography 
                        variant={isMobile ? 'h4' : 'h3'}
                        sx={{ 
                            fontWeight: 700,
                            color: theme.palette.text.primary,
                            marginBottom: 2,
                        }}
                    >
                        View Our Range
                    </Typography>
                    <Typography 
                        variant='body1'
                        sx={{ 
                            color: theme.palette.text.secondary,
                            maxWidth: 600,
                            margin: '0 auto',
                        }}
                    >
                        Everything you need for your business, delivered fresh to your door
                    </Typography>
                </Box>

                <Box sx={{
                    display: 'flex',
                    flexDirection: isTablet ? 'column' : 'row',
                    gap: 4,
                    justifyContent: 'center',
                    alignItems: isTablet ? 'center' : 'stretch',
                }}>
                    {services.map((service, index) => (
                        <Box
                            key={service.title}
                            onClick={service.action}
                            sx={{
                                flex: 1,
                                maxWidth: isTablet ? 400 : 380,
                                width: '100%',
                                padding: isMobile ? 3 : 4,
                                borderRadius: 4,
                                backgroundColor: theme.palette.background.default,
                                cursor: 'pointer',
                                transition: 'all 0.4s ease',
                                border: `1px solid ${theme.palette.divider}`,
                                opacity: isInView ? 1 : 0,
                                animation: isInView ? `fadeInUp 0.8s ease-out ${0.2 + index * 0.15}s forwards` : 'none',
                                animationFillMode: 'backwards',
                                '&:hover': {
                                    transform: 'translateY(-8px)',
                                    boxShadow: `0 20px 40px ${config.primaryColor}15`,
                                    borderColor: config.primaryColor,
                                },
                            }}
                        >
                            <Box sx={{
                                width: isMobile ? 70 : 80,
                                height: isMobile ? 70 : 80,
                                borderRadius: 3,
                                backgroundColor: `${config.primaryColor}15`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginBottom: 3,
                                color: config.primaryColor,
                                transition: 'all 0.3s ease',
                            }}>
                                {service.icon}
                            </Box>
                            <Typography 
                                variant='h5'
                                sx={{ 
                                    fontWeight: 600,
                                    marginBottom: 1.5,
                                    color: theme.palette.text.primary,
                                }}
                            >
                                {service.title}
                            </Typography>
                            <Typography 
                                variant='body2'
                                sx={{ 
                                    color: theme.palette.text.secondary,
                                    lineHeight: 1.7,
                                    marginBottom: 2,
                                }}
                            >
                                {service.description}
                            </Typography>
                            <Typography
                                variant='body2'
                                sx={{
                                    color: config.primaryColor,
                                    fontWeight: 600,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                }}
                            >
                                Explore →
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
};

// About Section
const AboutSection = ({ theme, config, isMobile, isTablet }: any) => {
    const { ref, isInView } = useInView(0.1);

    return (
        <Box
            id='about-section'
            ref={ref}
            sx={{
                py: isMobile ? 8 : 12,
                px: isMobile ? 2 : 4,
                backgroundColor: theme.palette.background.default,
            }}
        >
            <Box sx={{
                maxWidth: 1200,
                margin: '0 auto',
                display: 'flex',
                flexDirection: isTablet ? 'column' : 'row',
                alignItems: 'center',
                gap: isMobile ? 4 : 8,
            }}>
                {/* Image/Visual Side */}
                <Box
                    sx={{
                        flex: 1,
                        opacity: isInView ? 1 : 0,
                        animation: isInView ? 'fadeInLeft 0.8s ease-out forwards' : 'none',
                    }}
                >
                    <Box sx={{
                        width: '100%',
                        maxWidth: 500,
                        aspectRatio: '4/3',
                        borderRadius: 4,
                        background: `linear-gradient(135deg, ${config.primaryColor}20 0%, ${config.primaryColor}40 100%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        overflow: 'hidden',
                    }}>
                        <AgricultureIcon sx={{ 
                            fontSize: isMobile ? 100 : 150, 
                            color: config.primaryColor,
                            opacity: 0.8,
                        }} />
                        {/* Decorative elements */}
                        <Box sx={{
                            position: 'absolute',
                            top: 20,
                            right: 20,
                            width: 60,
                            height: 60,
                            borderRadius: '50%',
                            backgroundColor: `${config.primaryColor}30`,
                        }} />
                        <Box sx={{
                            position: 'absolute',
                            bottom: 30,
                            left: 30,
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            backgroundColor: `${config.primaryColor}40`,
                        }} />
                    </Box>
                </Box>

                {/* Content Side */}
                <Box
                    sx={{
                        flex: 1,
                        opacity: isInView ? 1 : 0,
                        animation: isInView ? 'fadeInRight 0.8s ease-out 0.2s forwards' : 'none',
                        animationFillMode: 'backwards',
                    }}
                >
                    <Typography 
                        variant='overline'
                        sx={{ 
                            color: config.primaryColor,
                            fontWeight: 600,
                            letterSpacing: 3,
                            marginBottom: 1,
                            display: 'block',
                        }}
                    >
                        About Us
                    </Typography>
                    <Typography 
                        variant={isMobile ? 'h4' : 'h3'}
                        sx={{ 
                            fontWeight: 700,
                            color: theme.palette.text.primary,
                            marginBottom: 3,
                            lineHeight: 1.3,
                        }}
                    >
                        Fruit & Vegetable Wholesale Supplier
                    </Typography>
                    <Typography 
                        variant='body1'
                        sx={{ 
                            color: theme.palette.text.secondary,
                            marginBottom: 3,
                            lineHeight: 1.9,
                        }}
                    >
                        We are committed to promoting healthy lifestyles at the most affordable prices, 
                        making wellness and healthy eating accessible to everyone.
                    </Typography>
                    <Typography 
                        variant='body1'
                        sx={{ 
                            color: theme.palette.text.secondary,
                            marginBottom: 4,
                            lineHeight: 1.9,
                        }}
                    >
                        At Cape Roots, we proudly offer a wide selection of fresh, locally sourced 
                        produce for businesses and households. Our commitment to high-quality, 
                        sustainably grown fruits and vegetables supports local farmers and artisans.
                    </Typography>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {[
                            'Locally sourced, sustainably grown',
                            'Supporting local farmers & artisans',
                            'Fresh seasonal produce year-round',
                        ].map((item, index) => (
                            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <VerifiedIcon sx={{ color: config.primaryColor, fontSize: 22 }} />
                                <Typography variant='body2' sx={{ color: theme.palette.text.primary, fontWeight: 500 }}>
                                    {item}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

// Why Choose Us Section
const WhyChooseUsSection = ({ theme, config, isMobile, isTablet }: any) => {
    const { ref, isInView } = useInView(0.1);

    const features = [
        {
            icon: <VerifiedIcon sx={{ fontSize: 32 }} />,
            title: 'Premium Quality',
            description: 'Only the freshest, highest-grade produce makes it to your business.',
        },
        {
            icon: <LocalShippingIcon sx={{ fontSize: 32 }} />,
            title: 'Fast Delivery',
            description: 'Convenient delivery throughout Cape Town CBD, starting from 4AM.',
        },
        {
            icon: <AgricultureIcon sx={{ fontSize: 32 }} />,
            title: 'Local Sourcing',
            description: 'We support local farmers and ensure sustainable growing practices.',
        },
    ];

    return (
        <Box
            ref={ref}
            sx={{
                py: isMobile ? 8 : 12,
                px: isMobile ? 2 : 4,
                backgroundColor: theme.palette.background.paper,
            }}
        >
            <Box sx={{ maxWidth: 1200, margin: '0 auto' }}>
                <Box
                    sx={{
                        textAlign: 'center',
                        marginBottom: isMobile ? 5 : 8,
                        opacity: isInView ? 1 : 0,
                        animation: isInView ? 'fadeInUp 0.8s ease-out forwards' : 'none',
                    }}
                >
                    <Typography 
                        variant='overline'
                        sx={{ 
                            color: config.primaryColor,
                            fontWeight: 600,
                            letterSpacing: 3,
                            marginBottom: 1,
                            display: 'block',
                        }}
                    >
                        Why Cape Roots
                    </Typography>
                    <Typography 
                        variant={isMobile ? 'h4' : 'h3'}
                        sx={{ 
                            fontWeight: 700,
                            color: theme.palette.text.primary,
                        }}
                    >
                        Your Trusted Partner
                    </Typography>
                </Box>

                <Box sx={{
                    display: 'flex',
                    flexDirection: isTablet ? 'column' : 'row',
                    gap: 4,
                    justifyContent: 'center',
                    alignItems: isTablet ? 'center' : 'stretch',
                }}>
                    {features.map((feature, index) => (
                        <Box
                            key={feature.title}
                            sx={{
                                flex: 1,
                                maxWidth: isTablet ? 400 : 350,
                                width: '100%',
                                textAlign: 'center',
                                padding: isMobile ? 3 : 4,
                                opacity: isInView ? 1 : 0,
                                animation: isInView ? `scaleIn 0.6s ease-out ${0.2 + index * 0.15}s forwards` : 'none',
                                animationFillMode: 'backwards',
                            }}
                        >
                            <Box sx={{
                                width: 70,
                                height: 70,
                                borderRadius: '50%',
                                backgroundColor: `${config.primaryColor}15`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 20px',
                                color: config.primaryColor,
                            }}>
                                {feature.icon}
                            </Box>
                            <Typography 
                                variant='h6'
                                sx={{ 
                                    fontWeight: 600,
                                    marginBottom: 1.5,
                                    color: theme.palette.text.primary,
                                }}
                            >
                                {feature.title}
                            </Typography>
                            <Typography 
                                variant='body2'
                                sx={{ 
                                    color: theme.palette.text.secondary,
                                    lineHeight: 1.7,
                                }}
                            >
                                {feature.description}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
};

// Contact Section
const ContactSection = ({ theme, config, isMobile, isTablet }: any) => {
    const { ref, isInView } = useInView(0.1);

    const contactInfo = [
        {
            icon: <PhoneIcon sx={{ fontSize: 24 }} />,
            title: 'Call Us',
            details: ['Nicholas: 078 586 6662'],
        },
        {
            icon: <EmailIcon sx={{ fontSize: 24 }} />,
            title: 'Email Us',
            details: ['Orders: orders@caperoots.co.za', 'Accounts: accounts@caperoots.co.za'],
        },
        {
            icon: <AccessTimeIcon sx={{ fontSize: 24 }} />,
            title: 'Operating Hours',
            details: ['Monday - Friday', '4AM - 6PM'],
        },
        {
            icon: <LocationOnIcon sx={{ fontSize: 24 }} />,
            title: 'Location',
            details: ['100 Gunners Circle, Epping', 'Cape Town, 7475'],
        },
    ];

    return (
        <Box
            ref={ref}
            sx={{
                py: isMobile ? 8 : 12,
                px: isMobile ? 2 : 4,
                backgroundColor: theme.palette.background.default,
            }}
        >
            <Box sx={{ maxWidth: 1200, margin: '0 auto' }}>
                <Box
                    sx={{
                        textAlign: 'center',
                        marginBottom: isMobile ? 5 : 8,
                        opacity: isInView ? 1 : 0,
                        animation: isInView ? 'fadeInUp 0.8s ease-out forwards' : 'none',
                    }}
                >
                    <Typography 
                        variant='overline'
                        sx={{ 
                            color: config.primaryColor,
                            fontWeight: 600,
                            letterSpacing: 3,
                            marginBottom: 1,
                            display: 'block',
                        }}
                    >
                        Get In Touch
                    </Typography>
                    <Typography 
                        variant={isMobile ? 'h4' : 'h3'}
                        sx={{ 
                            fontWeight: 700,
                            color: theme.palette.text.primary,
                            marginBottom: 2,
                        }}
                    >
                        Contact Details
                    </Typography>
                </Box>

                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: isTablet 
                        ? isMobile ? '1fr' : 'repeat(2, 1fr)' 
                        : 'repeat(4, 1fr)',
                    gap: 3,
                }}>
                    {contactInfo.map((info, index) => (
                        <Box
                            key={info.title}
                            sx={{
                                padding: 3,
                                borderRadius: 3,
                                backgroundColor: theme.palette.background.paper,
                                border: `1px solid ${theme.palette.divider}`,
                                textAlign: 'center',
                                opacity: isInView ? 1 : 0,
                                animation: isInView ? `fadeInUp 0.6s ease-out ${0.1 + index * 0.1}s forwards` : 'none',
                                animationFillMode: 'backwards',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    borderColor: config.primaryColor,
                                    transform: 'translateY(-4px)',
                                },
                            }}
                        >
                            <Box sx={{
                                width: 50,
                                height: 50,
                                borderRadius: '50%',
                                backgroundColor: `${config.primaryColor}15`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 16px',
                                color: config.primaryColor,
                            }}>
                                {info.icon}
                            </Box>
                            <Typography 
                                variant='subtitle1'
                                sx={{ 
                                    fontWeight: 600,
                                    marginBottom: 1,
                                    color: theme.palette.text.primary,
                                }}
                            >
                                {info.title}
                            </Typography>
                            {info.details.map((detail, i) => (
                                <Typography 
                                    key={i}
                                    variant='body2'
                                    sx={{ 
                                        color: theme.palette.text.secondary,
                                        lineHeight: 1.6,
                                    }}
                                >
                                    {detail}
                                </Typography>
                            ))}
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
};

// CTA Section
const CTASection = ({ theme, config, isMobile, onShopNow }: any) => {
    const { ref, isInView } = useInView(0.2);

    return (
        <Box
            ref={ref}
            sx={{
                py: isMobile ? 8 : 10,
                px: isMobile ? 3 : 4,
                background: `linear-gradient(135deg, ${config.primaryColor} 0%, ${config.primaryColor}dd 100%)`,
                textAlign: 'center',
            }}
        >
            <Box sx={{ maxWidth: 700, margin: '0 auto' }}>
                <Typography 
                    variant={isMobile ? 'h4' : 'h3'}
                    sx={{ 
                        fontWeight: 700,
                        color: 'white',
                        marginBottom: 2,
                        opacity: isInView ? 1 : 0,
                        animation: isInView ? 'fadeInUp 0.8s ease-out forwards' : 'none',
                    }}
                >
                    Ready to Order?
                </Typography>
                <Typography 
                    variant='body1'
                    sx={{ 
                        color: 'rgba(255,255,255,0.9)',
                        marginBottom: 4,
                        maxWidth: 500,
                        margin: '0 auto 32px',
                        opacity: isInView ? 1 : 0,
                        animation: isInView ? 'fadeInUp 0.8s ease-out 0.2s forwards' : 'none',
                        animationFillMode: 'backwards',
                    }}
                >
                    Browse our selection of fresh, locally sourced produce and place your order today.
                </Typography>
                <Button
                    variant='contained'
                    size='large'
                    onClick={onShopNow}
                    sx={{
                        backgroundColor: 'white',
                        color: config.primaryColor,
                        '&:hover': {
                            backgroundColor: 'rgba(255,255,255,0.9)',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 8px 25px rgba(0,0,0,0.2)',
                        },
                        paddingX: isMobile ? 5 : 6,
                        paddingY: 1.5,
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        borderRadius: 3,
                        transition: 'all 0.3s ease',
                        opacity: isInView ? 1 : 0,
                        animation: isInView ? 'fadeInUp 0.8s ease-out 0.4s forwards' : 'none',
                        animationFillMode: 'backwards',
                    }}
                >
                    Shop Now
                </Button>
            </Box>
        </Box>
    );
};

const styles = StyleSheet.create({
    scrollContent: {
        flexGrow: 1,
    },
});

export default CapeRootsHome;
